import React, { forwardRef, useCallback } from 'react';
import CompletionsEditableTable, { ColumnConfig, EditableTableRef } from './CompletionsEditableTable';
import { ValveData } from '../../../../models/CompletionModels';
import { valveTypes, injectionTypes, valvesSizes } from './CompletionDropdownData';
import { useAppDispatch } from '../../../../hooks/storeHooks';
import { validateValvesCompletion } from '../../../../services/well-completions/WellCompletionsService';

interface CompletionsValvesTabProps {
  data?: ValveData[];
}

export const fixValvePayload = (valve: any, index: number) => ({
  ...valve,
  Id: 0,
  Index: index,
  MandrelNumber: String(valve.MandrelNumber),
  DepthMeasured: String(valve.DepthMeasured),
  DepthTrueVertical: String(valve.DepthTrueVertical),
  Type: +valve.Type,
  InjectionType: +valve.InjectionType,
  Size: +valve.Size,
  Port: +valve.Port,
  TestRackOpeningPressureAt60Degrees: +valve.TestRackOpeningPressureAt60Degrees,
  RVal: +valve.RVal,
});

const CompletionsValvesTab = forwardRef<EditableTableRef, CompletionsValvesTabProps>(({ data }, ref) => {
  const dispatch = useAppDispatch();

  const validateValveData = useCallback(
    async (rowData: ValveData, allTableData: ValveData[] = data || []): Promise<string[]> => {
      try {
        const apiPayload = fixValvePayload(rowData, -1);
        const existingIndex = allTableData.findIndex((item) => item.Id === rowData.Id);

        const payload =
          existingIndex >= 0
            ? allTableData.map((item, i) => fixValvePayload(i === existingIndex ? rowData : item, i))
            : [apiPayload, ...allTableData.map((item, i) => fixValvePayload(item, i))];

        const result = await dispatch(validateValvesCompletion(payload)).unwrap();

        if (!result || (typeof result === 'object' && !Array.isArray(result) && !Object.keys(result).length)) {
          return [];
        }

        if (Array.isArray(result)) return result.filter((e) => typeof e === 'string' && e.trim());
        if (typeof result === 'object')
          return Object.values(result)
            .filter((v) => typeof v === 'string' && v.trim())
            .map(String);
        if (typeof result === 'string' && result.trim()) return [result];

        return ['Validation failed'];
      } catch (err) {
        console.error('Valve validation error:', err);
        return ['Validation service unavailable'];
      }
    },
    [dispatch],
  );

  const valveColumns: ColumnConfig[] = [
    { key: 'MandrelNumber', header: 'Mandrel #', type: 'input', isNumeric: true },
    { key: 'DepthMeasured', header: 'Depth MD(ft)', type: 'input', isNumeric: true },
    { key: 'DepthTrueVertical', header: 'Depth VD (ft)', type: 'input', isNumeric: true },
    { key: 'Manufacturer', header: 'Valve Manf', type: 'input' },
    { key: 'Type', header: 'Valve Type', type: 'select', options: valveTypes },
    { key: 'Name', header: 'Valve Name', type: 'input' },
    { key: 'InjectionType', header: 'Injection Type', type: 'select', options: injectionTypes },
    { key: 'Size', header: 'Valve Size', type: 'select', options: valvesSizes },
    { key: 'Port', header: 'Port Size', type: 'input' },
    { key: 'TestRackOpeningPressureAt60Degrees', header: 'TRO60', type: 'input', isNumeric: true },
    { key: 'RVal', header: 'Rval', type: 'input', isNumeric: true },
  ];

  const createNewValveRow = (newId: number, defaultData?: any): ValveData => {
    const defaultValues = defaultData || {};

    return {
      Id: newId,
      Index: defaultValues.Index || 0,
      MandrelNumber: defaultValues.MandrelNumber || 0,
      DepthMeasured: defaultValues.DepthMeasured || 0,
      DepthTrueVertical: defaultValues.DepthTrueVertical || 0,
      Manufacturer: defaultValues.Manufacturer || '',
      Type: defaultValues.Type ?? valveTypes[0]?.key ?? '',
      Name: defaultValues.Name || '',
      InjectionType: defaultValues.InjectionType ?? injectionTypes[1]?.key ?? '',
      Size: defaultValues.Size ?? valvesSizes[0]?.key ?? '',
      Port: defaultValues.Port || 1,
      TestRackOpeningPressureAt60Degrees: defaultValues.TestRackOpeningPressureAt60Degrees || 0,
      RVal: defaultValues.RVal || 0,
    };
  };

  const sortedData = data ? [...data].sort((a, b) => Number(a.MandrelNumber) - Number(b.MandrelNumber)) : data;

  return (
    <CompletionsEditableTable
      ref={ref}
      title='Valves'
      data={sortedData}
      columns={valveColumns}
      createNewRow={createNewValveRow}
      className='completions-bore-tab-table'
      validateRow={validateValveData}
      validateOnSaveOnly={true}
    />
  );
});

export default CompletionsValvesTab;
